import '../css/app.css';

/**
 * Behaviour for the utility-class WordPress menus. The legacy site.js menu
 * handlers intentionally remain untouched; these selectors only match the
 * dynamically rendered Evox primary menu walkers.
 */
(() => {
  const toArray = (nodes) => Array.prototype.slice.call(nodes);
  const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const focusWithoutScroll = (element) => {
    if (!element) {
      return;
    }

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  };

  const directChild = (element, selector) => toArray(element.children).find((child) => child.matches(selector)) || null;

  const getMenuItemId = (item) => {
    const idClass = toArray(item.classList).find((name) => /^menu-item-\d+$/.test(name));
    return idClass ? idClass.slice('menu-item-'.length) : '';
  };

  const initDesktopMenus = () => {
    toArray(document.querySelectorAll('.evox-primary-menu-nav')).forEach((nav) => {
      const menu = directChild(nav, '.evox-primary-menu') || nav.querySelector('.evox-primary-menu');

      if (!menu || nav.dataset.evoxPrimaryReady === 'true') {
        return;
      }

      nav.dataset.evoxPrimaryReady = 'true';

      const records = [];
      toArray(menu.querySelectorAll('li.menu-item-has-children')).forEach((item, index) => {
        const trigger = directChild(item, 'a');
        const submenu = directChild(item, 'ul');

        if (!trigger || !submenu) {
          return;
        }

        let submenuId = submenu.id;
        if (!submenuId) {
          const itemId = getMenuItemId(item) || String(index + 1);
          submenuId = `evox-primary-submenu-${itemId}`;
          submenu.id = submenuId;
        }

        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-controls', submenuId);
        if (!trigger.hasAttribute('aria-expanded')) {
          trigger.setAttribute('aria-expanded', 'false');
        }
        if (!submenu.hasAttribute('aria-hidden')) {
          submenu.setAttribute('aria-hidden', 'true');
        }

        records.push({
          item,
          trigger,
          submenu,
          open: false,
          skipFocus: false,
        });
      });

      if (!records.length) {
        return;
      }

      const findRecord = (item) => records.find((record) => record.item === item) || null;

      const setOpen = (record, open) => {
        record.open = open;
        record.item.classList.toggle('is-open', open);
        record.item.classList.toggle('is-closed', !open);
        record.trigger.setAttribute('aria-expanded', String(open));
        record.submenu.setAttribute('aria-hidden', String(!open));
      };

      const closeRecord = (record) => {
        records.forEach((child) => {
          if (child !== record && record.item.contains(child.item) && child.open) {
            closeRecord(child);
          }
        });
        setOpen(record, false);
      };

      const closeOthers = (record) => {
        records.forEach((other) => {
          // Keep the branch leading to the item being opened, but close all
          // siblings and any stale descendant panels.
          if (other === record || other.item.contains(record.item)) {
            return;
          }
          if (other.open || !other.item.classList.contains('is-closed')) {
            closeRecord(other);
          }
        });
      };

      const openRecord = (record) => {
        closeOthers(record);
        records.forEach((ancestor) => {
          if (ancestor !== record && ancestor.item.contains(record.item)) {
            setOpen(ancestor, true);
          }
        });
        record.skipFocus = false;
        setOpen(record, true);
      };

      // Synchronize server-rendered attributes and ensure focus-within can be
      // explicitly suppressed after Escape or a click-to-close action.
      records.forEach((record) => {
        const initiallyOpen = record.item.classList.contains('is-open')
          || record.trigger.getAttribute('aria-expanded') === 'true';
        setOpen(record, initiallyOpen);
      });

      records.forEach((record) => {
        const isPlaceholder = () => {
          const href = record.trigger.getAttribute('href');
          return !href || href.trim() === '#';
        };

        record.item.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') {
            return;
          }
          openRecord(record);
        });

        record.item.addEventListener('pointerleave', (event) => {
          const nextTarget = event.relatedTarget;
          window.setTimeout(() => {
            if (record.item.contains(nextTarget)
              || record.submenu.contains(nextTarget)
              || record.submenu.matches(':hover')
              || record.item.matches(':hover')
              || record.item.matches(':focus-within')) {
              return;
            }
            closeRecord(record);
          }, 80);
        });

        record.item.addEventListener('focusin', (event) => {
          const focusedItem = event.target && event.target.closest
            ? event.target.closest('li.menu-item-has-children')
            : null;
          if (focusedItem && focusedItem !== record.item) {
            return;
          }

          if (record.skipFocus) {
            record.skipFocus = false;
            return;
          }
          openRecord(record);
        });

        record.item.addEventListener('focusout', (event) => {
          const nextTarget = event.relatedTarget;
          window.setTimeout(() => {
            if (record.item.contains(nextTarget) || record.item.matches(':hover') || record.item.matches(':focus-within')) {
              return;
            }
            closeRecord(record);
          }, 0);
        });

        record.trigger.addEventListener('click', (event) => {
          if (!isPlaceholder()) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          if (record.open) {
            closeRecord(record);
          } else {
            openRecord(record);
          }
        });

        record.trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            openRecord(record);
            const firstFocusable = record.submenu.querySelector(focusableSelector);
            window.requestAnimationFrame(() => {
              if (record.open) {
                focusWithoutScroll(firstFocusable);
              }
            });
            return;
          }

          if (event.key === 'Escape') {
            if (!record.open) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            const triggerIsFocused = document.activeElement === record.trigger;
            closeRecord(record);
            // If focus was inside the submenu, the generated focusin event on
            // the trigger must not immediately reopen the branch.
            record.skipFocus = !triggerIsFocused;
            focusWithoutScroll(record.trigger);
            return;
          }

          const isSpace = event.key === ' ' || event.key === 'Spacebar';
          if (isSpace || (event.key === 'Enter' && isPlaceholder())) {
            event.preventDefault();
            event.stopPropagation();
            if (record.open) {
              closeRecord(record);
            } else {
              openRecord(record);
            }
          }
        });
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        let item = event.target && event.target.closest
          ? event.target.closest('li.menu-item-has-children')
          : null;
        let record = null;

        while (item && !record) {
          const candidate = findRecord(item);
          if (candidate && candidate.open) {
            record = candidate;
            break;
          }
          item = item.parentElement
            ? item.parentElement.closest('li.menu-item-has-children')
            : null;
        }

        if (!record) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        const triggerIsFocused = document.activeElement === record.trigger;
        closeRecord(record);
        record.skipFocus = !triggerIsFocused;
        focusWithoutScroll(record.trigger);
      });

      document.addEventListener('click', (event) => {
        if (nav.contains(event.target)) {
          return;
        }
        records.forEach((record) => closeRecord(record));
      });
    });
  };

  const initMobileMenus = () => {
    // A configured WordPress menu is wrapped by .evox-mobile-primary-menu;
    // the two header fallbacks render their own details tree. Initialise the
    // drawer root only so nested details do not receive duplicate listeners.
    const drawerMenus = toArray(document.querySelectorAll('.home-mobile-menu, .mirror-mobile-menu'));
    const menus = drawerMenus.length
      ? drawerMenus
      : toArray(document.querySelectorAll('.evox-mobile-primary-menu'));

    menus.forEach((menu) => {
      if (menu.dataset.evoxMobileReady === 'true') {
        return;
      }

      menu.dataset.evoxMobileReady = 'true';

      toArray(menu.querySelectorAll('details')).forEach((details, index) => {
        const summary = directChild(details, 'summary');
        const panel = directChild(details, '.evox-mobile-sub-menu') || directChild(details, 'div');

        if (!summary || !panel) {
          return;
        }

        let panelId = panel.id;
        if (!panelId) {
          panelId = `evox-mobile-submenu-${index + 1}`;
          panel.id = panelId;
        }

        summary.setAttribute('aria-controls', panelId);

        const syncState = () => {
          summary.setAttribute('aria-expanded', String(details.open));
          panel.setAttribute('aria-hidden', String(!details.open));
        };

        syncState();
        details.addEventListener('toggle', syncState);
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        const details = event.target && event.target.closest
          ? event.target.closest('details')
          : null;
        if (!details || !menu.contains(details) || !details.open) {
          return;
        }

        const summary = directChild(details, 'summary');
        event.preventDefault();
        event.stopPropagation();
        details.open = false;
        focusWithoutScroll(summary);
      });
    });
  };

  /**
   * The legacy drawer controller moves focus when opening. Pointer focus can
   * be applied after the click handler in some browsers, so verify the result
   * after layout settles and only intervene when focus stayed outside.
   */
  const initMobileDrawerFocus = () => {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');

    if (!toggle || !menu) {
      return;
    }

    toggle.addEventListener('click', () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (toggle.getAttribute('aria-expanded') !== 'true' || menu.contains(document.activeElement)) {
            return;
          }

          const target = menu.querySelector('[data-mobile-menu-close]')
            || menu.querySelector(focusableSelector);
          focusWithoutScroll(target);
        });
      });
    });
  };

  // Native details handles the interaction for language menus. Keep the
  // expanded state mirrored to ARIA so desktop and mobile output expose the
  // same state to assistive technology.
  const initLanguageSwitchers = () => {
    toArray(document.querySelectorAll('details[data-language-switcher]')).forEach((details) => {
      const summary = directChild(details, 'summary');

      if (!summary || details.dataset.evoxLanguageReady === 'true') {
        return;
      }

      details.dataset.evoxLanguageReady = 'true';
      summary.setAttribute('aria-haspopup', 'true');

      const syncState = () => {
        summary.setAttribute('aria-expanded', String(details.open));
      };

      syncState();
      details.addEventListener('toggle', syncState);
    });
  };

  /**
   * The fallback headers are intentionally still handled by legacy site.js
   * for backwards compatibility. Keep their ARIA panel state in sync here so
   * hover/focus and click-to-close actions are exposed correctly to assistive
   * technology without registering a second interaction controller.
   */
  const initFallbackMenuState = () => {
    toArray(document.querySelectorAll('.home-nav-group, .home-nav-subgroup, .mirror-nav-group, .mirror-nav-subgroup')).forEach((group) => {
      const trigger = directChild(group, 'a[aria-haspopup], button[aria-haspopup]');
      const panel = directChild(group, '.home-dropdown, .mirror-dropdown');

      if (!trigger || !panel || group.dataset.evoxAriaReady === 'true') {
        return;
      }

      group.dataset.evoxAriaReady = 'true';

      const sync = () => {
        const forcedClosed = group.dataset.menuClosed === 'true';
        const expanded = !forcedClosed && (
          trigger.getAttribute('aria-expanded') === 'true'
          || group.matches(':hover')
          || group.matches(':focus-within')
        );
        panel.setAttribute('aria-hidden', String(!expanded));
      };

      ['pointerenter', 'pointerleave', 'focusin', 'focusout'].forEach((eventName) => {
        group.addEventListener(eventName, () => window.setTimeout(sync, 0));
      });

      const observer = new MutationObserver(sync);
      observer.observe(group, {
        attributes: true,
        attributeFilter: ['class', 'data-menu-closed', 'aria-expanded'],
        subtree: true,
      });
      sync();
    });
  };

  const initAboutIntroCarousels = () => {
    const carousels = toArray(document.querySelectorAll('[data-about-carousel]'));

    carousels.forEach((carousel) => {
      if (carousel.dataset.evoxCarouselReady === 'true') {
        return;
      }

      const slides = toArray(carousel.querySelectorAll('[data-about-carousel-slide]'));
      if (!slides.length) {
        return;
      }

      const dots = toArray(carousel.querySelectorAll('[data-about-carousel-dot]'));
      const previous = carousel.querySelector('[data-about-carousel-prev]');
      const next = carousel.querySelector('[data-about-carousel-next]');
      let activeIndex = 0;
      let timer;
      let resizeTimer;

      carousel.dataset.evoxCarouselReady = 'true';

      const getSlideHeight = (slide) => {
        if (!slide) {
          return 0;
        }

        const frameWidth = carousel.clientWidth;
        if (frameWidth > 0 && slide.naturalWidth > 0 && slide.naturalHeight > 0) {
          return frameWidth * slide.naturalHeight / slide.naturalWidth;
        }

        const measuredHeight = slide.getBoundingClientRect().height;
        return measuredHeight > 0 ? measuredHeight : 0;
      };

      const resizeFrame = () => {
        const height = getSlideHeight(slides[activeIndex]);
        if (height > 0) {
          carousel.style.height = Math.ceil(height) + 'px';
        }
      };

      const prepareFrame = () => {
        if (getSlideHeight(slides[0]) <= 0) {
          return;
        }

        resizeFrame();
        slides.forEach((slide) => {
          slide.classList.add('absolute', 'inset-x-0', 'top-0');
          slide.classList.remove('relative');
        });
        carousel.classList.remove('min-h-[120px]');
        carousel.dataset.evoxCarouselPrepared = 'true';
      };

      const render = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        resizeFrame();
        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle('opacity-100', isActive);
          slide.classList.toggle('opacity-0', !isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });

        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.setAttribute('aria-selected', String(isActive));
          const indicator = dot.querySelector('span');
          indicator?.classList.toggle('bg-white', isActive);
          indicator?.classList.toggle('bg-white/60', !isActive);
        });
      };

      const stop = () => {
        window.clearInterval(timer);
        timer = undefined;
      };

      const start = () => {
        stop();
        if (slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return;
        }
        timer = window.setInterval(() => render(activeIndex + 1), 5000);
      };

      previous?.addEventListener('click', () => {
        render(activeIndex - 1);
        start();
      });
      next?.addEventListener('click', () => {
        render(activeIndex + 1);
        start();
      });
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          render(Number(dot.dataset.aboutCarouselDot) || 0);
          start();
        });
      });

      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', (event) => {
        if (!carousel.contains(event.relatedTarget)) {
          start();
        }
      });

      slides.forEach((slide) => {
        slide.addEventListener('load', () => {
          if (carousel.dataset.evoxCarouselPrepared !== 'true') {
            prepareFrame();
          }
          resizeFrame();
        });
      });

      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(resizeFrame, 80);
      }, { passive: true });

      render(0);
      prepareFrame();
      start();
    });
  };

  const initBpcFaq = () => {
    const faqItems = toArray(document.querySelectorAll('[data-bpc-faq-item]'));

    if (!faqItems.length) {
      return;
    }

    const updateOpenPanels = () => {
      faqItems.forEach((item) => {
        const trigger = item.querySelector('[data-bpc-faq-trigger]');
        const panel = item.querySelector('[data-bpc-faq-panel]');
        if (!trigger || !panel || trigger.getAttribute('aria-expanded') !== 'true') {
          return;
        }

        panel.style.maxHeight = `${panel.scrollHeight}px`;
      });
    };

    faqItems.forEach((item) => {
      if (item.dataset.evoxFaqReady === 'true') {
        return;
      }

      const trigger = item.querySelector('[data-bpc-faq-trigger]');
      const panel = item.querySelector('[data-bpc-faq-panel]');
      const icon = item.querySelector('[data-bpc-faq-icon]');

      if (!trigger || !panel) {
        return;
      }

      item.dataset.evoxFaqReady = 'true';

      const setOpen = (open) => {
        trigger.setAttribute('aria-expanded', String(open));
        panel.setAttribute('aria-hidden', String(!open));
        panel.toggleAttribute('inert', !open);
        icon?.classList.toggle('rotate-180', open);

        if (open) {
          panel.classList.remove('opacity-0');
          panel.classList.add('opacity-100');
          panel.style.maxHeight = `${panel.scrollHeight}px`;
          return;
        }

        panel.classList.remove('opacity-100');
        panel.classList.add('opacity-0');
        panel.style.maxHeight = '0px';
      };

      panel.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'max-height' && trigger.getAttribute('aria-expanded') === 'true') {
          panel.style.maxHeight = 'none';
        }
      });

      trigger.addEventListener('click', () => {
        setOpen(trigger.getAttribute('aria-expanded') !== 'true');
      });

      setOpen(false);
    });

    window.addEventListener('resize', updateOpenPanels);
  };

  // The legacy preview forms create their status paragraph at submit time.
  // Keep the semantic hook used by the old script, while decorating the
  // runtime node with the same utility classes as the server-rendered forms.
  const formStatusClasses = [
    'mt-4',
    'rounded-lg',
    'border',
    'border-emerald-200',
    'bg-emerald-50',
    'px-4',
    'py-3',
    'text-sm',
    'leading-6',
    'text-emerald-800',
  ];

  const decorateFormStatus = (form) => {
    const status = form && form.querySelector('[aria-live="polite"].mirror-form-success');
    if (status) {
      status.classList.add(...formStatusClasses);
    }
  };

  let formErrorId = 0;

  const focusFirstFormError = (form) => {
    if (!form) {
      return;
    }

    const candidates = toArray(form.querySelectorAll(
      '[aria-invalid="true"]:not([type="hidden"]), '
      + '.ff-el-is-error input:not([type="hidden"]), '
      + '.ff-el-is-error select, '
      + '.ff-el-is-error textarea'
    ));
    const field = candidates.find((candidate) => candidate.getClientRects().length > 0 && !candidate.disabled);

    if (!field) {
      return;
    }

    const group = field.closest('.ff-el-group');
    const error = group && group.querySelector('.error[role="alert"], .text-danger[role="alert"]');
    if (error) {
      if (!error.id) {
        formErrorId += 1;
        error.id = `evox-form-error-${formErrorId}`;
      }

      const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(error.id);
      field.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    }

    focusWithoutScroll(field);
  };

  const initDynamicContent = () => {
    toArray(document.querySelectorAll('form[action="#"]')).forEach(decorateFormStatus);
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.matches && form.matches('form[action="#"]')) {
        window.setTimeout(() => decorateFormStatus(form), 0);
      }
    });

    if (window.jQuery) {
      window.jQuery(document).on(
        'fluentform_validation_failed.evoxFocus fluentform_submission_failed.evoxFocus',
        'form.frm-fluent-form',
        function handleFluentFormFailure() {
          const form = this;
          window.setTimeout(() => focusFirstFormError(form), 0);
        }
      );
    }
  };

  const init = () => {
    initDesktopMenus();
    initMobileMenus();
    initMobileDrawerFocus();
    initLanguageSwitchers();
    initFallbackMenuState();
    initAboutIntroCarousels();
    initBpcFaq();
    initDynamicContent();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
